import i18n from '@/i18n';
import {
  absenceEndDate,
  absenceStartDate,
} from '@/lib/absence/absenceFunctions';
import {
  leaveEndDate,
  leaveStartDate,
} from '@/lib/leave/leaveRequestFunctions';
import { ShiftPeriodInterface } from '@/types/shift';
import { keyOfFirstTruthyValue } from '@/util/objectFunctions';
import { Absence, LeaveRequest, Shift, Unavailability } from '../../../api/v1';
import { isPublished } from './shiftV2Status';

export type ConflictingCustomShiftMessage = {
  title: string;
  unavailability: Unavailability[];
  leave: LeaveRequest[];
  absence: Absence[];
  shifts: Shift[];
};

export const getConflictingCustomShiftMessage = (
  dateRanges: ShiftPeriodInterface[],
  numberOfShifts: number,
  employeeName: string,
  employeeUnavailability: Unavailability[],
  employeeLeave: LeaveRequest[],
  employeeAbsences: Absence[],
  employeeShifts: Shift[],
  timezone: string,
  shiftId: number = null,
  onlyPublishedShiftConflicts: boolean = false,
): ConflictingCustomShiftMessage | null => {
  const unavailability = employeeUnavailability.filter((u) =>
    dateRanges.find(
      (dateRange) =>
        u.startsAt < dateRange.endsAt && u.endsAt > dateRange.startsAt,
    ),
  );

  const leave = employeeLeave.filter((l) =>
    dateRanges.find(
      (dateRange) =>
        leaveStartDate(l, timezone) < dateRange.endsAt &&
        leaveEndDate(l, timezone) > dateRange.startsAt,
    ),
  );

  // Only check closed absences, employees are technically still available with open absences
  const absence = employeeAbsences.filter(
    (absence) =>
      !!absence.endedOn &&
      dateRanges.find(
        (dateRange) =>
          absenceStartDate(absence, timezone) < dateRange.endsAt &&
          absenceEndDate(absence, timezone) > dateRange.startsAt,
      ),
  );

  const shifts = employeeShifts.filter((shift) =>
    dateRanges.find(
      (dateRange) =>
        (!shiftId || shift.id !== shiftId) &&
        (!onlyPublishedShiftConflicts || isPublished(shift)) &&
        shift.startsAt < dateRange.endsAt &&
        shift.endsAt > dateRange.startsAt,
    ),
  );

  if (![...unavailability, ...leave, ...absence, ...shifts].length) {
    return null;
  }

  const title =
    (keyOfFirstTruthyValue({
      [i18n.tc('schedule.error.employeeHasLeave', 1, {
        employee: employeeName,
      })]: !!leave.length,
      [i18n.tc('schedule.error.employeeHasUnavailability', 1, {
        employee: employeeName,
      })]: !!unavailability.length,
      [i18n.tc('schedule.error.employeeHasAbsence', 1, {
        employee: employeeName,
      })]: !!absence.length,
      [i18n.tc('schedule.error.conflictWithOtherShifts', numberOfShifts, {
        employee: employeeName,
      })]: !!shifts.length,
    }) as string) || '';

  return {
    title,
    unavailability,
    leave,
    absence,
    shifts,
  };
};
